import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Grid,
  CircularProgress,
  Avatar,
  Typography,
  Divider,
  Paper,
} from '@mui/material';
import { phoneRegExp } from '@/app/utils/validation.ts';
import { useTranslation } from 'react-i18next';
import { I18nNamespace } from '@/i18n/types/i18nNamespace.ts';
import type { I18nCommonNs, I18nValidationNs } from '@/i18n/dictionaries/interfaces.ts';
import { useDispatch, useSelector } from 'react-redux';
import * as UserSelectors from 'app/selectors/userSelectors.ts';
import { useAccountEditUserProfile } from '@/api-gen/api.ts';
import type { EditUserProfileCommand } from '@/api-gen/api.schemas';
import { LoadingStatus } from 'app/store/types.ts';
import { UserActions } from 'app/actions/user/userAction.ts';
import { useMsal } from '@azure/msal-react';
import { Person, Phone as PhoneIcon } from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

interface EditProfileDialogProps {
  open: boolean;
  onClose: () => void;
}

const editProfileSchema = z.object({
  name: z.string().min(1, { message: nameof.full<I18nValidationNs>((n) => n.fieldRequired) }),
  phone: z.string().refine((value) => !value || phoneRegExp.test(value), {
    message: nameof.full<I18nValidationNs>((n) => n.phoneNumberInvalid)
  }),
});

type FormData = z.infer<typeof editProfileSchema>;

const EditProfileDialog: React.FC<EditProfileDialogProps> = ({ open, onClose }) => {
  const [translate] = useTranslation(I18nNamespace.Common);
  const [translateValidation] = useTranslation(I18nNamespace.Validation);
  const currentUser = useSelector(UserSelectors.CurrentUserSelector);
  const dispatch = useDispatch();
  const userLoadingStatus = useSelector(UserSelectors.CurrentUserLoadingStatusSelector);
  const [isFetchingUser, setIsFetchingUser] = React.useState(false);
  const { accounts } = useMsal();
  const [userPhoto, setUserPhoto] = React.useState<string>('');

  // Function to get user initials for avatar fallback
  const getUserInitials = React.useCallback((name: string) => {
    return name
      .split(' ')
      .map((part) => part[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  }, []);

  const { control, handleSubmit, reset, formState } = useForm<FormData>({
    defaultValues: {
      name: '',
      phone: '',
    },
    resolver: zodResolver(editProfileSchema)
  });

  React.useEffect(() => {
    // In a real implementation, you would use the actual photo from Azure.
    // For now we'll simulate it being set from accounts data
    if (currentUser?.name) {
      // This is a placeholder - in a real implementation you would retrieve the actual photo
      setUserPhoto('');
    }
  }, [currentUser?.name]);

  React.useEffect(() => {
    if (isFetchingUser && userLoadingStatus === LoadingStatus.Succeeded) {
      setIsFetchingUser(false);
      onClose();
    }
  }, [isFetchingUser, userLoadingStatus, onClose]);

  React.useEffect(() => {
    if (open && currentUser) {
      reset({
        name: currentUser.name || '',
        phone: currentUser.phone || '',
      });
    }
  }, [open, currentUser, reset]);

  const { mutateAsync, isLoading } = useAccountEditUserProfile({
    mutation: {
      onSuccess: () => {
        dispatch(UserActions.getCurrentUser());
        setIsFetchingUser(true);
      }
    }
  });

  const onSubmit = async (data: FormData) => {
    try {
      const payload: EditUserProfileCommand = {
        name: data.name,
        phone: data.phone || null
      };
      await mutateAsync({
        data: payload
      });
    } catch (error: unknown) {
      console.error('Error updating profile:', error);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle sx={{ pb: 1 }}>{translate('common:editProfile')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3, mt: 2 }}>
            <Avatar
              src={userPhoto}
              sx={{
                width: 100,
                height: 100,
                mb: 2,
                bgcolor: 'primary.main',
                fontSize: '2rem',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
              }}
            >
              {!userPhoto && currentUser?.name ? getUserInitials(currentUser.name) : <Person fontSize="large" />}
            </Avatar>
            <Typography variant="subtitle1" fontWeight="bold" textAlign="center">
              {currentUser?.name}
            </Typography>
            {currentUser?.email && (
              <Typography variant="body2" color="text.secondary" textAlign="center">
                {currentUser.email}
              </Typography>
            )}
            {currentUser?.companyName && (
              <Typography variant="body2" color="text.secondary" textAlign="center">
                {currentUser.companyName}
                {currentUser?.departmentName && ` • ${currentUser.departmentName}`}
              </Typography>
            )}
          </Box>

          <Divider sx={{ mb: 3 }} />

          <Paper
            elevation={0}
            sx={{
              p: 3,
              backgroundColor: 'rgba(0,0,0,0.02)',
              borderRadius: 2
            }}
          >
            <Typography variant="h6" sx={{ mb: 2 }}>
              {translate('common:profile.contactInformation')}
            </Typography>

            <Grid container direction="column" spacing={3}>
              <Grid item>
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={translate('common:profile.name')}
                      fullWidth
                      error={!!formState.errors.name}
                      helperText={
                        formState.errors.name?.message
                          ? translateValidation(formState.errors.name.message as string)
                          : undefined
                      }
                      InputProps={{
                        startAdornment: <Person color="action" sx={{ mr: 1 }} />
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item>
                <Controller
                  name="phone"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={translate('common:profile.phone')}
                      fullWidth
                      error={!!formState.errors.phone}
                      helperText={
                        formState.errors.phone?.message
                          ? translateValidation(formState.errors.phone.message as string)
                          : undefined
                      }
                      InputProps={{
                        startAdornment: <PhoneIcon color="action" sx={{ mr: 1 }} />
                      }}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Paper>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose} variant="outlined" sx={{ borderRadius: 2 }}>
            {translate('common:buttonLabels.cancel')}
          </Button>
          <Button variant="contained" type="submit" disabled={isLoading || isFetchingUser} sx={{ borderRadius: 2 }}>
            {isLoading || isFetchingUser ? (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <span style={{ marginRight: 8 }}>{translate('common:buttonLabels.save')}</span>
                <CircularProgress size={20} color="inherit" />
              </Box>
            ) : (
              translate('common:buttonLabels.save')
            )}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default EditProfileDialog;
